

































































































































import {
  defineComponent, computed, watch, reactive, ref,
} from '@vue/composition-api';
import { useQuery, useResult, useMutation } from '@vue/apollo-composable';
import { useRouter } from '@ideavivat/vivat-2';

import log from '@/table/log';

import CommonDropzoneWithDocumentUpload from '@/components/CommonDropzoneWithDocumentUpload.vue';
import CommonDropzoneWithImageUpload from '@/components/CommonDropzoneWithImageUpload.vue';
import CommonMediaTypeSelector from '@/components/Selector/CommonMediaTypeSelector.vue';
import CommonTagsMultiSelector from '@/components/Selector/CommonTagsMultiSelector.vue';
import CommonFileTable from '@/components/Table/CommonFileTable.vue';
import CommonToggleButton from '@/components/CommonCustomButton/CommonToggleButton.vue';
import CommonImageTable from '@/components/Table/CommonImageTable.vue';
import CommonConfirmDialog from '@/components/Dialog/CommonConfirmDialog.vue';
import ViewOnlyGuard from '@/components/ViewOnlyGuard.vue';

import getMedia from '@/gql/getMedia.gql';
import createMedia from '@/gql/createMedia.gql';
import updateMedia from '@/gql/updateMedia.gql';

const MediaEditor = defineComponent({
  setup() {
    /* ==== Get meta data ==== */
    const { route } = useRouter();
    // eslint-disable-next-line
    const mode = route.value.meta.mode;
    const modeDisplay = computed(() => {
      if (mode === 'create') return 'เพิ่ม';
      if (mode === 'edit') return 'แก้ไข';
      if (mode === 'view') return 'ดู';
      return '';
    });
    // Try to destructor but doesn't work Q^Q

    /* ===== Menu manager ===== */
    const editorMenu = [
      { id: 0, status: true, display: 'ประเภทสื่อ' },
      { id: 1, status: false, display: 'ข้อมูลสื่อ' },
      { id: 2, status: false, display: 'อัปโหลดไฟล์หรือลิ้งค์วิดิโอ' },
      { id: 3, status: false, display: 'ประวัติการแก้ไข' },
    ];

    function changeActiveMenu(index: number) {
      for (let i = 0; i < editorMenu.length; i += 1) {
        if (editorMenu[index].id === i) editorMenu[i].status = true;
        else editorMenu[i].status = false;
      }
    }
    // =========================== //

    const form = reactive({
      // ==== [0] Detail ==== //
      type: '1',
      // ==== [1] Detail ==== //
      name: '',
      description: '',
      tags: [],
      // ==== [2] Video ==== //
      youtubeLink: '',
      images: [],
      documents: [],
      // ==== [3] Logs ==== //
      logs: [],
      tableViewMode: 'images',
    });

    const mediaType = [
      { id: 1, display: 'วิดิโอ' },
      { id: 2, display: 'หนังสือ' },
      { id: 3, display: 'นิตยสาร' },
    ];

    // eslint-disable-next-line
    const computedMenuDisplay = computed(() => {
      for (let i = 0; i < mediaType.length; i += 1) {
        if (mediaType[i].id === parseInt(form.type, 10)) {
          return mediaType[i];
        }
      }
      return mediaType[1];
    });

    const computedImages = computed(() => {
      const temp: any = [];
      // eslint-disable-next-line
      form.images?.forEach((element: any) => {
        temp.push(parseInt(element.id, 10));
      });
      return temp;
    });

    /* ==== Validate to submit button ==== */
    const validated = computed(() => {
      if (form.type === '' || form.type === undefined) return false;
      if (form.name === '' || form.name === undefined) return false;
      return true;
    });

    const saveModal = ref(null);
    function pendingSubmit() {
      if (validated.value) (saveModal.value as any).open();
    }
    // ========================= //

    /* ==== Get value when enter edit mode */
    const isQueryEnabled = ref(true);
    const { result: mediaResult } = useQuery(getMedia, computed(() => ({
      id: route.value.params.mediaId,
    })), () => ({
      fetchPolicy: 'network-only',
      enabled: isQueryEnabled.value,
    }));
    watch(mediaResult, () => {
      const media = useResult(mediaResult, null, (data) => data.media);
      const item = media.value;
      if (item) isQueryEnabled.value = false; // if value is loaded, disable next query
      form.name = item?.name;
      form.type = item?.mediaType.id || '1';
      form.description = item?.description;
      form.tags = item?.tags || [];
      form.logs = item?.logs;
      form.youtubeLink = item?.youtubeLink || '';
      form.images = item?.images || [];
      form.documents = item?.documents || [];
    });

    // ------------------------- //
    // ===== Mutation Zone ===== //
    // ------------------------- //
    const createMediaMutation = useMutation(createMedia,
      () => ({
        variables: {
          mediaType: parseInt(form.type, 10),
          name: form.name,
          description: form.description || '',
          tags: form.tags || [],
          images: computedImages.value,
          documents: form.documents.map((doc: {}) => parseInt((doc as any).id, 10)),
          youtubeLink: form.youtubeLink,
        },
        refetchQueries: ['getMedias'],
      }));

    const updateMediaMutation = useMutation(updateMedia,
      () => ({
        variables: {
          id: route.value.params.mediaId,
          mediaType: parseInt(form.type, 10),
          name: form.name,
          description: form.description || '',
          tags: form.tags || [],
          images: computedImages.value,
          documents: form.documents.map((doc: {}) => parseInt((doc as any).id, 10)),
          youtubeLink: form.youtubeLink,
        },
        refetchQueries: ['getMedias'],
      }));

    const computedMutation = computed(() => {
      if (mode === 'create') return createMediaMutation;
      return updateMediaMutation;
    });
    // ======================== //

    function onImageUploadDone(newImage: never) {
      if (!form.images) form.images = [];
      form.images.push(newImage);
    }

    function onDocumentUploadDone(newDocument: never) {
      if (!form.documents) form.documents = [];
      form.documents.push(newDocument);
    }

    return {
      mediaType,
      onImageUploadDone,
      onDocumentUploadDone,
      mode,
      editorMenu,
      changeActiveMenu,
      computedMenuDisplay,
      form,
      log,
      computedMutation,
      modeDisplay,
      validated,
      pendingSubmit,
      saveModal,
    };
  },
  components: {
    CommonDropzoneWithDocumentUpload,
    CommonDropzoneWithImageUpload,
    CommonMediaTypeSelector,
    CommonTagsMultiSelector,
    CommonFileTable,
    CommonToggleButton,
    CommonImageTable,
    CommonConfirmDialog,
    ViewOnlyGuard,
  },
  props: {
    role: {},
  },
});

export default MediaEditor;
